export interface WhatsAppBillingBlock {
  channelId: string;
  channelName: string;
  phone: string | null;
  failedCount: number;
  firstFailedAt: string;
  lastFailedAt: string;
  isPartial: boolean;
}

export interface WhatsAppBillingBlockSummary {
  numbers: number;
  failedCount: number;
  since: string;
  isPartial: boolean;
  numberLabel: string;
}

export interface WhatsAppBillingBlockCopy {
  headline: string;
  cause: string;
  impact: string;
  action: string;
}

const CLEAR_BLOCK_ROLES = new Set(["admin", "supervisor"]);

export function canClearChannelBlock(role: string | null | undefined, isSuperAdmin: boolean): boolean {
  return isSuperAdmin || CLEAR_BLOCK_ROLES.has(String(role ?? ""));
}

export function parseBillingBlockRows(rows: unknown): WhatsAppBillingBlock[] {
  if (!Array.isArray(rows)) return [];
  return rows
    .map((row) => {
      const r = (row ?? {}) as Record<string, unknown>;
      return {
        channelId: String(r.channel_id ?? ""),
        channelName: String(r.channel_name ?? ""),
        phone: r.phone_number ? String(r.phone_number) : null,
        failedCount: Number(r.failed_count) || 0,
        firstFailedAt: String(r.first_failed_at ?? ""),
        lastFailedAt: String(r.last_failed_at ?? ""),
        isPartial: r.is_partial === true,
      };
    })
    .filter((b) => b.channelId && b.failedCount > 0 && !Number.isNaN(Date.parse(b.firstFailedAt)));
}

export function summarizeBillingBlocks(blocks: WhatsAppBillingBlock[]): WhatsAppBillingBlockSummary | null {
  if (blocks.length === 0) return null;
  const earliest = blocks.reduce((a, b) => (Date.parse(b.firstFailedAt) < Date.parse(a.firstFailedAt) ? b : a));
  const only = blocks[0];
  return {
    numbers: blocks.length,
    failedCount: blocks.reduce((sum, b) => sum + b.failedCount, 0),
    since: earliest.firstFailedAt,
    isPartial: blocks.some((b) => b.isPartial),
    numberLabel: blocks.length === 1 ? `del número ${only.phone || only.channelName}` : `de ${blocks.length} de tus números`,
  };
}

function dayKey(date: Date, timeZone?: string) {
  return new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).format(date);
}

export function formatBlockedSince(iso: string, now: Date = new Date(), timeZone?: string): string {
  const at = new Date(iso);
  const time = new Intl.DateTimeFormat("es-CO", { timeZone, hour: "numeric", minute: "2-digit", hour12: true }).format(at);
  if (dayKey(at, timeZone) === dayKey(now, timeZone)) return `hoy, ${time}`;
  const day = new Intl.DateTimeFormat("es-CO", { timeZone, day: "numeric", month: "long" }).format(at);
  return `el ${day}, ${time}`;
}

export function billingBlockCopy(
  summary: WhatsAppBillingBlockSummary,
  options: { isAdmin: boolean; now?: Date; timeZone?: string },
): WhatsAppBillingBlockCopy {
  const count = summary.failedCount.toLocaleString("es-CO");
  const since = formatBlockedSince(summary.since, options.now, options.timeZone);
  return {
    headline: summary.isPartial
      ? "WhatsApp no está entregando parte de tus mensajes."
      : "WhatsApp no está entregando tus respuestas.",
    cause: `${summary.isPartial ? "Meta está rechazando envíos" : "Meta bloqueó los envíos"} ${summary.numberLabel} porque la cuenta de WhatsApp Business no tiene un método de pago válido o tiene un saldo pendiente.`,
    impact:
      summary.failedCount === 1
        ? `Desde ${since}, va 1 mensaje sin entregar.`
        : `Desde ${since}, van ${count} mensajes sin entregar.`,
    action: options.isAdmin
      ? "Agrega o actualiza el método de pago en Meta para que vuelvan a salir."
      : "Pídele a un administrador de tu cuenta que agregue o actualice el método de pago en Meta para que vuelvan a salir.",
  };
}

export function clearBlockErrorMessage(error: unknown): string {
  const code = (error as { code?: string } | null)?.code;
  if (code === "42501") return "No tienes permiso para reintentar. Pídeselo a un administrador o supervisor.";
  if (code === "P0002") return "No encontramos ese número. Recarga la página e inténtalo de nuevo.";
  return "No se pudo reintentar. Inténtalo de nuevo en unos minutos.";
}
